import { useCallback, useEffect, useState, createRef, useRef } from 'react'
import { useEffectOnce } from 'usehooks-ts'
import styled from 'styled-components'
import dataDE from 'data.json'
import dataFR from 'data-fr.json'
import ColumnChart from 'components/ColumnChart'
import { nest } from 'd3-collection'

interface CountryWrapperProps {
  layout?: string
  isMobile: boolean
  colorMode?: string
}

const Country = ({
  tenant,
  colorMode,
  iso,
  titleText,
  htmlText,
  layout,
  colors,
}: {
  tenant?: object
  colorMode?: string
  iso: string
  titleText?: string
  htmlText?: string | TrustedHTML
  layout?: string
  colors: object
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const [activeTooltip, setActiveTooltip] = useState(-1)
  const [linkedText, setLinkedText] = useState('')

  const data = (tenant as { lang: string }).lang == 'fr' ? dataFR : dataDE

  const handleResize = (): void => {
    setIsMobile(window.innerWidth < 600)
  }

  useEffectOnce(() => {
    // set height when window is resized
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  })

  useEffect(() => {
    // split the htmlText into an array of strings
    const splitLinks = String(htmlText)?.split('[link href="')
    // console.log('splitLinks:', splitLinks)
    // console.log('splitLinks[1]:', splitLinks[1])
    // console.log('tenant:', tenant)
    const outputString = splitLinks.map((item: any, itemIndex: number) => {
      return itemIndex === 0
        ? item
        : item.includes('http')
        ? '<a href="' + item.replace('"]', '">').replace('[/link]', '</a>')
        : '<a href="https://www.' +
          (tenant as { key?: string })?.key +
          '.ch/' +
          item.replace('"]', '">').replace('[/link]', '</a>')
    })
    // console.log('outputString:', outputString)
    setLinkedText(outputString ? outputString.join('') : '')
  }, [])

  const filteredData = data.filter((item: any) => item.ISO === iso)
  const countryData = nest()
    .key(function (d: any) {
      return d['Land_DE']
    })
    .entries(filteredData)
  // console.log('countryData:', countryData)

  const yearData = nest()
    .key(function (d: any) {
      return d['Jahr']
    })
    .entries(countryData[0].values)
  // console.log('yearData', yearData)

  return (
    <CountryContainer>
      <CountryWrapper
        layout={layout}
        isMobile={isMobile}
        colorMode={colorMode}
        className={isMobile ? 'mobile' : 'desktop'}
      >
        {
          <h3 lang='de'>
            <span className='flag'>
              <img
                src={require('../../images/' +
                  iso.toLocaleLowerCase() +
                  '.svg')}
                alt={iso}
              />
            </span>
            <span
              className='name'
              dangerouslySetInnerHTML={{
                __html: titleText ? titleText : '',
              }}
            />{' '}
          </h3>
        }
        <div className='columns'>
          <div className='graphic-wrapper'>
            <ColumnChart
              data={yearData}
              index={0}
              colorMode={colorMode}
              colors={colors}
              iso={iso.toLowerCase()}
              activeTooltip={activeTooltip}
              setActiveTooltip={setActiveTooltip}
              isDetailView={true}
              isRightSide={layout === 'right'}
            />
          </div>
          <div
            className='txt'
            dangerouslySetInnerHTML={{
              __html: linkedText ? linkedText : '',
            }}
          />
        </div>
      </CountryWrapper>
    </CountryContainer>
  )
}

const CountryContainer = styled.div`
  background-color: var(--country-background);
`

const CountryWrapper = styled.div<CountryWrapperProps>`
  max-width: 1111px;
  margin: 0 auto;
  padding: 2rem 2rem 0.5rem;
  margin-bottom: 3rem;

  h3 {
    margin: 6rem 0 4rem;
    text-align: center;
  }

  .flag,
    .name {
      display: inline-block;
      @media only screen and (min-width: 601px) {
        font-size: 2.4rem;
      }
    }

    .flag {
      img {
        width: 1.5rem;
        height: auto;
        @media only screen and (min-width: 601px) {
          width: 2rem;
      }
        margin-right: 0.8rem;
        top: 0.2rem;
        position: relative;
        border: 1px solid var(--flag-border);
        filter: saturate(85%);
      }
    }

  .columns {
    display: grid;
    grid-template-columns: ${(props) =>
      props.layout === 'right' ? '2fr 1fr' : '1fr 2fr'};
    column-gap: 4rem;
    @media only screen and (max-width: 920px) {
      grid-template-columns: ${(props) =>
        props.layout === 'right' ? '1fr 1fr' : '1fr 1fr'};
    }
    @media only screen and (max-width: 600px) {
      grid-template-columns: 1fr;
    }
   

    .graphic-wrapper {
      order: ${(props) =>
        props.layout === 'right' && !props.isMobile ? 1 : -1};
      }
    }
    .txt {
      order: ${(props) =>
        props.layout === 'right' && !props.isMobile ? -1 : 1};
      font-family: var(--font-serif);

      a:active, a:link, a:visited {
        background-image: -webkit-gradient(linear,left top,right top,from(${(
          props
        ) => (props.colorMode === 'dark' ? '#9698a8' : '#ffe462')} ),to(${(
  props
) => (props.colorMode === 'dark' ? '#9698a8' : '#ffe462')}));
        background-image: -webkit-linear-gradient(left,${(props) =>
          props.colorMode === 'dark' ? '#9698a8' : '#ffe462'},${(props) =>
  props.colorMode === 'dark' ? '#9698a8' : '#ffe462'});
        background-image: linear-gradient(90deg,${(props) =>
          props.colorMode === 'dark' ? '#9698a8' : '#ffe462'},${(props) =>
  props.colorMode === 'dark' ? '#9698a8' : '#ffe462'});
        color: var(--text-color);
        background-repeat: repeat-x;
        background-position: bottom;
        background-size: 100% 15%;
        -webkit-transition: all .1s ease-in-out;
        transition: all .1s ease-in-out;
        
        background-origin: content-box;
        text-decoration: none;
      }
      a:hover {
        background-size: 100% 100%;
      }
    }
  }
`

export default Country
